import { useSyncExternalStore } from "react";

import type { ConversationReadMarks } from "~/ai-chat/model/conversation-read-marks";

/**
 * Client-side read receipts for conversations. The backend stamps `updated` on every
 * turn but has no per-user "seen" state, so the browser remembers when each
 * conversation was last on screen and a conversation counts as unread while its
 * `updated` is newer than that. Persisted per browser in localStorage; keyed by
 * conversation id, which carries no conversation content.
 */
const STORAGE_KEY = "istari.ai.conversation-read-marks";
const EMPTY: ConversationReadMarks = new Map();

let marks: ConversationReadMarks = load();
const listeners = new Set<() => void>();

export function useConversationReadMarks(): ConversationReadMarks {
  return useSyncExternalStore(
    subscribe,
    () => marks,
    () => EMPTY,
  );
}

export function markConversationRead(id: string, seenAt: number): void {
  if ((marks.get(id) ?? Number.NEGATIVE_INFINITY) >= seenAt) return;
  const next = new Map(marks);
  next.set(id, seenAt);
  marks = next;
  persist();
  emit();
}

export function clearConversationReadMarks(): void {
  marks = EMPTY;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // nothing to clear in storage if it's unavailable.
  }
  emit();
}

function load(): ConversationReadMarks {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return EMPTY;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return EMPTY;
    return new Map(Object.entries(parsed).filter((entry): entry is [string, number] => typeof entry[1] === "number"));
  } catch {
    // localStorage unavailable or corrupt — start with nothing read; marks accrue again as chats are opened.
    return EMPTY;
  }
}

function persist(): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(marks)));
  } catch {
    // localStorage unavailable (private mode / quota) — the in-memory marks still serve this page load.
  }
}

function emit(): void {
  for (const listener of listeners) listener();
}

// One window listener for the whole store, not one per subscriber, so a cross-tab
// write reloads and notifies once however many components are mounted.
function subscribe(callback: () => void): () => void {
  if (listeners.size === 0) window.addEventListener("storage", onStorage);
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
    if (listeners.size === 0) window.removeEventListener("storage", onStorage);
  };
}

// Another tab reading a conversation should clear its unread dot here too.
function onStorage(event: StorageEvent): void {
  if (event.key !== STORAGE_KEY) return;
  marks = load();
  emit();
}
